import { Button, CloseButton, Dialog, Portal, Stack, Text } from '@chakra-ui/react'
import {
  confirmationLine,
  formatIsoDateDE,
  formatValue,
  type PendingValueChange,
  type ValueKind,
} from '@/helper/valueChange.helper'

type ValueChangeConfirmDialogProps = {
  isOpen: boolean
  kind: ValueKind
  changes: PendingValueChange[]
  onCancel: () => void
  onConfirm: () => void | Promise<void>
  saving?: boolean
}

/**
 * The confirmation every price or capacity save goes through (SIB-941).
 *
 * Plain text, one line per changed service: service, old value, new value, effective date. No
 * thresholds - a capacity going from 2 to 4 is normal; the error this catches is a typo, and plain
 * text catches it. When a planned change is being edited, the line names the planned value it
 * replaces and a second line keeps the active value identifiable.
 */
export function ValueChangeConfirmDialog({
  isOpen,
  kind,
  changes,
  onCancel,
  onConfirm,
  saving = false,
}: ValueChangeConfirmDialogProps) {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={e => {
        if (!e.open && !saving) onCancel()
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-testid="value-change-confirm-dialog">
            <Dialog.Header>
              <Dialog.Title>Änderung speichern?</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton disabled={saving} />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>
              <Stack gap={4}>
                <Text>Bitte prüfen Sie die Änderung, bevor sie gespeichert wird:</Text>
                <Stack as="ul" gap={3} listStyleType="none">
                  {changes.map(change => (
                    <Stack as="li" key={change.serviceCode} gap={0}>
                      <Text>{confirmationLine(kind, change)}</Text>
                      {change.replaces && (
                        <Text color="fg.muted">
                          Ersetzt die geplante Änderung {formatValue(kind, change.replaces.value)}{' '}
                          ab {formatIsoDateDE(change.replaces.validFrom)}. Aktuell:{' '}
                          {formatValue(kind, change.activeValue)}.
                        </Text>
                      )}
                      {!change.validFrom && (
                        <Text color="fg.muted">
                          Erster Wert für diese Leistung, gilt ab sofort.
                        </Text>
                      )}
                    </Stack>
                  ))}
                </Stack>
                <Text>Speichern?</Text>
              </Stack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onCancel} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                variant="subtle"
                colorPalette="green"
                onClick={() => onConfirm()}
                loading={saving}
                disabled={saving}
              >
                Speichern
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
